import { useState } from "react";
import { Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { AddPhoto, AuthImg, btnLine, btnRed, dmy, F, field } from "@/components/stable/ui";

const EMPTY = { name: "", breed: "", sex: "", birth_date: "", color: "", microchip: "", passport: "", height_cm: "", feeding: "", notes: "" };
const SEX = { macho: "Macho", hembra: "Hembra", castrado: "Castrado" };

export const HorseForm = ({ horse, open, onClose, onSaved }) => {
  const [f, setF] = useState(horse ? { ...EMPTY, ...Object.fromEntries(Object.entries(horse).map(([k, v]) => [k, v ?? ""])) } : EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]])), height_cm: f.height_cm ? Number(f.height_cm) : null, birth_date: f.birth_date || null };
    try {
      if (horse) await api.put(`/horses/${horse.id}`, body); else await api.post("/horses", body);
      toast.success("Ficha guardada"); onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="horse-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B1D3A]">{horse ? `Editar ${horse.name}` : "Nuevo caballo"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <F label="Nombre *"><input required maxLength={60} value={f.name} onChange={set("name")} className={field} data-testid="horse-name" /></F>
          <F label="Raza"><input maxLength={60} value={f.breed} onChange={set("breed")} className={field} data-testid="horse-breed" /></F>
          <F label="Sexo"><select value={f.sex} onChange={set("sex")} className={field} data-testid="horse-sex"><option value="">—</option>{Object.entries(SEX).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
          <F label="Fecha de nacimiento"><input type="date" value={f.birth_date} onChange={set("birth_date")} className={field} data-testid="horse-birth" /></F>
          <F label="Capa"><input maxLength={40} value={f.color} onChange={set("color")} className={field} data-testid="horse-color" /></F>
          <F label="Alzada a la cruz (cm)"><input type="number" min="60" max="220" value={f.height_cm} onChange={set("height_cm")} className={field} data-testid="horse-height" /></F>
          <F label="Microchip"><input maxLength={40} value={f.microchip} onChange={set("microchip")} className={field} data-testid="horse-microchip" /></F>
          <F label="Pasaporte / LIC"><input maxLength={40} value={f.passport} onChange={set("passport")} className={field} data-testid="horse-passport" /></F>
          <F label="Alimentación" className="sm:col-span-2"><textarea rows={3} maxLength={1500} value={f.feeding} onChange={set("feeding")} placeholder="Ej.: 8 kg de heno, 2 kg de pienso en 3 tomas, Biotine 50 g/día" className={`${field} h-auto py-2`} data-testid="horse-feeding" /></F>
          <F label="Notas" className="sm:col-span-2"><textarea rows={2} maxLength={1500} value={f.notes} onChange={set("notes")} className={`${field} h-auto py-2`} data-testid="horse-notes" /></F>
          <button className={`${btnRed} sm:col-span-2`} data-testid="horse-save">Guardar ficha</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v }) => v ? <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 text-sm"><span className="text-slate-500">{k}</span><b className="text-right text-[#0B1D3A]">{v}</b></div> : null;

export const HorseCard = ({ h, reload, onEdit }) => {
  const last = h.weights?.at(-1);
  const del = async () => { if (window.confirm(`¿Borrar la ficha de ${h.name} con sus fotos y pesos?`)) { await api.delete(`/horses/${h.id}`); reload(); } };
  return (
    <article className="border border-slate-200 rounded p-5" data-testid={`stable-horse-${h.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div><h3 className="font-display font-black text-2xl uppercase text-[#0B1D3A]">{h.name}</h3><p className="text-sm text-slate-500">{[h.breed, SEX[h.sex], h.color].filter(Boolean).join(" · ")}</p></div>
        <div className="flex gap-2">
          <button onClick={onEdit} className="p-2 text-slate-500 hover:text-[#2563EB]" data-testid={`stable-horse-edit-${h.id}`}><Pencil size={17} /></button>
          <button onClick={del} className="p-2 text-slate-400 hover:text-[#DC2626]" data-testid={`stable-horse-delete-${h.id}`}><Trash2 size={17} /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-4">
        {Array.from({ length: h.photo_count || 0 }, (_, n) => (
          <div key={`${h.id}-${n}-${h.photo_count}`} className="relative group">
            <AuthImg path={`/horses/${h.id}/photo/${n}`} className="aspect-square w-full object-cover rounded" testid={`stable-horse-photo-${n}`} />
            <button onClick={async () => { await api.delete(`/horses/${h.id}/photo/${n}`); reload(); }} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X size={13} /></button>
          </div>
        ))}
        {(h.photo_count || 0) < 8 && <AddPhoto url={`/horses/${h.id}/photos`} onDone={reload} id={`stable-horse-add-photo-${h.id}`} max={8 - (h.photo_count || 0)} />}
      </div>
      <div className="mt-4">
        <Row k="Nacimiento" v={dmy(h.birth_date)} />
        <Row k="Alzada" v={h.height_cm && `${h.height_cm} cm`} />
        <Row k="Último peso" v={last && `${last.kg} kg (${new Date(last.date).toLocaleDateString("es-ES")})`} />
        <Row k="Microchip" v={h.microchip} />
        <Row k="Pasaporte / LIC" v={h.passport} />
      </div>
      {h.feeding && <p className="text-sm mt-3"><b className="text-[#0B1D3A]">Alimentación:</b> <span className="whitespace-pre-line text-slate-600">{h.feeding}</span></p>}
      {h.notes && <p className="text-sm mt-2 text-slate-600 whitespace-pre-line">{h.notes}</p>}
      <div className="flex flex-wrap gap-2 mt-4">
        <a href="/peso" className={`${btnLine} text-sm`}>Medir peso</a>
        <a href={`/calculadora?peso=${last?.kg || ""}`} className={`${btnLine} text-sm`}>Calcular dieta</a>
      </div>
    </article>
  );
};
